import { CancellationToken } from "@multiweb/types/multiweb";
import { Triplet } from "Global/Base/DataTypes/NTuples";
import { LogicalOperator } from "Global/Base/LogicalOperations";
import { IRightsContext } from "Global/Base/Rights/IRightsContext";
import { IOWObject } from "OW.Remoting/Remoting/DataTypes/IOWObject";
import { IForm } from "UI/UI/UI/XGControls/IForm";
import { IFrameWithRights } from "UI/UI/UI/XGControls/IFrame";
import { RequestContext } from "../../../../OW.Remoting/Remoting/RequestContext";
import { IClassItem as ClassItem, IEvaluatedConditionResult } from "../../../../OW.Remoting/Remoting/Rights";
import { IClassItemRights } from "../IClassItemRights";
import { IItemRights, RightType } from "../RightTypes";
import { StandardOperationRights } from "../StandardOperationRights";

/** Třída, která obsahuje různé implementace pro vyhodnocení práv.*/
export interface IRightsEvaluatorFactory {
    /**
     * Vrací instanci pro vyhodnocení práv podle zadaného kontextu
     */
    getContextEvaluator(context: IRightsContext): IRightsEvaluator;
}

export class IRightsEvaluatorFactory {
    private static _properties: (keyof IRightsEvaluatorFactory)[] = [];
    private static _methods: (keyof IRightsEvaluatorFactory)[] = ["getContextEvaluator"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) &&
            !this._methods.some(value => typeof instance[value] !== "function");
    }
}

/**
 * Vyhodnocovač práv a podmínek
 * @generateInstanceClass
 */
export interface IRightsEvaluator {
    /** Vyhodnotí serverové právo pro zadaný objekt */
    evaluateServerRight(classItem: ClassItem, rightToEvaluate: RightType, requestContext?: RequestContext, evaluateWithoutAO?: boolean): Promise<IClassItemRights | null>;
    /** Vyhodnotí podmínku */
    evaluateCondition(conditionID: string, isStatic: boolean, rightType: RightType, requestContext?: RequestContext): Promise<IEvaluatedConditionResult>;
    /** Vyhodnotí kolekci podmínek */
    evaluateConditions(conditionIDs: string[], areStatic: boolean, rightType: RightType, requestContext?: RequestContext): Promise<IEvaluatedConditionResult[]>;
    /** Vyhodnotí právo pro danou úroveň práv */
    evaluateLevelSubsetItemRights(rightType: RightType, itemRights: ReadonlyArray<IItemRights>, cachedConditions: Map<string, Promise<IEvaluatedConditionResult>>,
        isStatic: boolean | null, frame: IFrameWithRights, ct: CancellationToken, roleIds?: string[]): Promise<Triplet<boolean | null, string[] | null, string[] | null>>;
    /** Vrací logický operátor používaný pro kombinování výsledků daného typu práva */
    getOperatorForRightType(rightType: RightType): LogicalOperator;
    /** Vrací výchozí hodnotu pro daný typ práva */
    getDefaultRightValueForRightType(rightType: RightType): boolean;

    /**
     * Vyhodnotí a vrátí výsledek práv IED - Insert, Edit, Delete
     * Neměla by se volat přímo, ale z this.evaluateRights s příslušnými parametry
     * @param rightType Typ práva, které se má vyhodnotit
     * @param context Kontext, nad kterým se mají práva vyhodnotit
     * @param evaluateFormRights Formulářová práva by se měla aplikovat, pokud je frame hlavním framem nebo pokud je to detail k hlavnímu seznamu
     * @param cancellationToken
     */
    evaluateIEDRights(rightType: RightType, ownerForm: IForm, frame: IFrameWithRights, evaluateFormRights: boolean, cancellationToken?: CancellationToken): Promise<StandardOperationRights>;
}

export class IRightsEvaluator {
    private static _properties: (keyof IRightsEvaluator)[] = [];
    private static _methods: (keyof IRightsEvaluator)[] = ["evaluateServerRight", "evaluateCondition", "evaluateConditions", "evaluateIEDRights"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) &&
            !this._methods.some(value => typeof instance[value] !== "function");
    }
}

export interface IRightsParams {
    readonly evaluateFormRights: boolean;
    readonly objectName: string;
    readonly masterObject: IOWObject;
    readonly masterObjectInstanceID: string | null;
    readonly masterLinkToAttr: string;
}
